import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '../../lib/api';
import { EyeOff, AlertTriangle, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';

const REPORT_REASON_LABELS = {
  fraudulent: 'Annonce frauduleuse', misleading_photos: 'Photos trompeuses',
  abusive_price: 'Prix abusif', inappropriate: 'Contenu inapproprié', other: 'Autre',
};
const REPORT_RESOLUTION_LABELS = {
  dismissed: 'Rejeté', listing_hidden: 'Annonce masquée',
  listing_blocked: 'Annonce bloquée', listing_deleted: 'Annonce supprimée',
};

const ReportsTab = () => {
  const [reports, setReports] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [resolving, setResolving] = useState(null);

  const load = useCallback(() => {
    const params = { page, limit: 20 };
    if (statusFilter) params.status = statusFilter;
    adminAPI.getReports(params).then(r => { setReports(r.data.reports); setTotal(r.data.total); }).catch(() => {});
  }, [page, statusFilter]);
  useEffect(load, [load]);

  const handleResolve = async (reportId, action) => {
    if (!window.confirm(action === 'dismissed' ? 'Rejeter ce signalement ?' : 'Confirmer cette action ?')) return;
    setResolving(reportId);
    try { await adminAPI.resolveReport(reportId, action); load(); } catch {} finally { setResolving(null); }
  };

  return (
    <div data-testid="admin-reports">
      <div className="flex flex-wrap gap-2 mb-4">
        {[{ value: 'pending', label: 'En attente' }, { value: 'resolved', label: 'Traités' }, { value: '', label: 'Tous' }].map(s => (
          <button key={s.value} onClick={() => { setStatusFilter(s.value); setPage(1); }}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${statusFilter === s.value ? 'bg-[#0C63BF] text-white' : 'bg-white border border-[#D6E8F0] text-[#5A8BA8] hover:bg-[#F0F7FA]'}`}
            data-testid={`report-filter-${s.value || 'all'}`}>
            {s.label}
          </button>
        ))}
        <span className="ml-auto text-xs text-[#5A8BA8] self-center">{total} signalement{total !== 1 ? 's' : ''}</span>
      </div>
      <div className="space-y-2">
        {reports.map(r => (
          <div key={r.id} className={`bg-white rounded-xl border p-3 sm:p-4 ${r.status === 'pending' ? 'border-red-200' : 'border-[#E8F4FD]'}`} data-testid={`admin-report-${r.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${r.status === 'pending' ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'}`}>
                    {r.status === 'pending' ? 'En attente' : 'Traité'}
                  </span>
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-orange-100 text-orange-600">
                    {REPORT_REASON_LABELS[r.reason] || r.reason}
                  </span>
                </div>
                <p className="text-sm font-semibold text-[#033871] mt-1.5">{r.listing_brand} {r.listing_model}</p>
                <p className="text-[11px] text-[#5A8BA8]">Vendeur : {r.seller_name} &middot; Signalé par : {r.reporter_name}</p>
                {r.details && <p className="text-[11px] text-[#5A8BA8] mt-1 italic">"{r.details}"</p>}
                <p className="text-[10px] text-[#B8D4E3] mt-1">
                  {new Date(r.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                  {r.resolution && <span className="ml-2 text-green-600 font-medium">&rarr; {REPORT_RESOLUTION_LABELS[r.resolution] || r.resolution}</span>}
                </p>
              </div>
              {r.status === 'pending' && (
                <div className="flex flex-wrap items-center gap-1 shrink-0">
                  <button onClick={() => handleResolve(r.id, 'dismissed')} disabled={resolving === r.id} className="px-2.5 py-1.5 rounded-lg text-[11px] font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-50">Rejeter</button>
                  <button onClick={() => handleResolve(r.id, 'listing_hidden')} disabled={resolving === r.id} className="px-2.5 py-1.5 rounded-lg text-[11px] font-medium bg-yellow-100 text-yellow-700 hover:bg-yellow-200 disabled:opacity-50"><EyeOff className="w-3.5 h-3.5 inline mr-0.5" /> Masquer</button>
                  <button onClick={() => handleResolve(r.id, 'listing_blocked')} disabled={resolving === r.id} className="px-2.5 py-1.5 rounded-lg text-[11px] font-medium bg-orange-100 text-orange-700 hover:bg-orange-200 disabled:opacity-50"><AlertTriangle className="w-3.5 h-3.5 inline mr-0.5" /> Bloquer</button>
                  <button onClick={() => handleResolve(r.id, 'listing_deleted')} disabled={resolving === r.id} className="px-2.5 py-1.5 rounded-lg text-[11px] font-medium bg-red-100 text-red-600 hover:bg-red-200 disabled:opacity-50"><Trash2 className="w-3.5 h-3.5 inline mr-0.5" /> Supprimer</button>
                </div>
              )}
            </div>
          </div>
        ))}
        {reports.length === 0 && <p className="text-center text-sm text-[#5A8BA8] py-8">{statusFilter === 'pending' ? 'Aucun signalement en attente' : 'Aucun signalement'}</p>}
      </div>
      {total > 20 && (
        <div className="flex justify-center items-center gap-3 mt-4">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="p-1.5 rounded-lg border disabled:opacity-30"><ChevronLeft className="w-4 h-4" /></button>
          <span className="text-sm text-[#5A8BA8]">Page {page}</span>
          <button onClick={() => setPage(p => p + 1)} disabled={reports.length < 20} className="p-1.5 rounded-lg border disabled:opacity-30"><ChevronRight className="w-4 h-4" /></button>
        </div>
      )}
    </div>
  );
};

export default ReportsTab;
